import { useCallback, useMemo } from 'react';
import useSWR from 'swr';
import { WORKDAY_EVENT_TYPE, type WorkdayEventType } from 'constants/workday';
import { getTimeClockHistoryDateRange } from 'helpers/workdayHelpers';
import { useWorkdayLogHoursModal } from 'hooks/useWorkdayLogHoursModal';
import { useHybridToken } from 'providers/Auth';
import {
  getTimeClockEventsHistory,
  type TimeClockEventsHistoryResponse,
} from 'providers/Workday';
import { useUserProfile } from 'providers/UserProfile';

type RecentEventStatus = 'loading' | 'loaded' | 'error';

type RecentTimeClockEventState = {
  recentEventStatus: RecentEventStatus;
  recentEventType: WorkdayEventType | null;
  recentEventDateTime: string;
  recentTimeEntryCodeDescriptor: string | undefined;
  isRecentEventError: boolean;
  isRecentEventLoading: boolean;
  refreshRecentEvent: () => Promise<void>;
};

const parseMostRecentEvent = (response: TimeClockEventsHistoryResponse) => {
  const [mostRecent] = response?.data ?? [];
  // With no history in range, the user is treated as checked out.
  if (!mostRecent) {
    return {
      eventType: WORKDAY_EVENT_TYPE.CLOCK_OUT,
      dateTime: '',
      timeEntryCodeDescriptor: undefined,
    };
  }

  return {
    eventType: mostRecent.eventType.descriptor as WorkdayEventType,
    dateTime: mostRecent.dateTime,
    timeEntryCodeDescriptor: mostRecent.timeEntryCode?.descriptor ?? undefined,
  };
};

const useRecentTimeClockEvent = (
  enabled: boolean,
): RecentTimeClockEventState => {
  const { hybridToken } = useHybridToken();
  const { userProfile } = useUserProfile();
  const timezoneId = userProfile?.timezoneId;
  const { setStatusRefreshing } = useWorkdayLogHoursModal();

  const dateRange = useMemo(
    () => (timezoneId ? getTimeClockHistoryDateRange(timezoneId) : null),
    [timezoneId],
  );

  const swrKey = useMemo(
    () =>
      enabled && hybridToken && dateRange
        ? [
            'timeClockEventsHistory',
            hybridToken,
            dateRange.fromDate,
            dateRange.toDate,
          ]
        : null,
    [enabled, hybridToken, dateRange],
  );

  const { data, error, mutate } = useSWR(
    swrKey,
    () =>
      getTimeClockEventsHistory(hybridToken!, {
        fromDate: dateRange!.fromDate,
        toDate: dateRange!.toDate,
      }),
    { revalidateOnFocus: false },
  );

  const parsed = data ? parseMostRecentEvent(data) : null;

  const recentEventStatus: RecentEventStatus = (() => {
    if (!timezoneId) return 'error';
    if (error) return 'error';
    if (parsed) return 'loaded';
    return 'loading';
  })();

  const refreshRecentEvent = useCallback(async () => {
    if (!swrKey) return;

    setStatusRefreshing(true);
    try {
      await mutate();
    } finally {
      setStatusRefreshing(false);
    }
  }, [mutate, setStatusRefreshing, swrKey]);

  return {
    recentEventStatus,
    recentEventType: parsed?.eventType ?? null,
    recentEventDateTime: parsed?.dateTime ?? '',
    recentTimeEntryCodeDescriptor: parsed?.timeEntryCodeDescriptor,
    isRecentEventError: recentEventStatus === 'error',
    isRecentEventLoading: recentEventStatus === 'loading',
    refreshRecentEvent,
  };
};

export { useRecentTimeClockEvent };
export type { RecentEventStatus };
